import React, { useRef, useEffect, useCallback } from 'react';
import { marked } from 'marked';
import { htmlToMarkdown } from '../../utils/markdown';
import {
  FiBold,
  FiItalic,
  FiList,
  FiMinus,
} from 'react-icons/fi';
import { LuHeading2, LuHeading3, LuListOrdered } from 'react-icons/lu';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  compact?: boolean;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  value,
  onChange,
  placeholder = 'Escriba aquí...',
  minHeight = '150px',
  compact = false,
}) => {
  const editorNodeRef = useRef<HTMLDivElement | null>(null);
  const isInitializedRef = useRef(false);
  const lastValueRef = useRef(value);

  // Inicializar el contenido cuando el valor cambia externamente
  useEffect(() => {
    if (editorNodeRef.current && value !== lastValueRef.current) {
      const html = marked.parse(value || '') as string;
      editorNodeRef.current.innerHTML = html;
      lastValueRef.current = value;
      isInitializedRef.current = true;
    }
  }, [value]);

  const setEditorRef = useCallback((node: HTMLDivElement | null) => {
    editorNodeRef.current = node;
    if (node && !isInitializedRef.current) {
      const html = marked.parse(value || '') as string;
      node.innerHTML = html;
      isInitializedRef.current = true;
      lastValueRef.current = value;
    }
  }, [value]);

  const applyFormat = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorNodeRef.current?.focus();
  }, []);

  const handleInput = useCallback(() => {
    if (editorNodeRef.current) {
      // Serializar de vuelta a markdown: `innerText` devolvía texto plano y borraba
      // el formato que la barra de herramientas acababa de aplicar.
      const text = htmlToMarkdown(editorNodeRef.current.innerHTML);
      lastValueRef.current = text;
      onChange(text);
    }
  }, [onChange]);

  return (
    <div className={`wysiwyg-editor-container ${compact ? 'compact' : ''}`}>
      <div className="wysiwyg-toolbar">
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => applyFormat('bold')}
            title="Negrita"
          >
            <FiBold />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => applyFormat('italic')}
            title="Cursiva"
          >
            <FiItalic />
          </button>
        </div>

        {!compact && (
          <>
            <div className="toolbar-divider" />
            <div className="toolbar-group">
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => applyFormat('formatBlock', 'h2')}
                title="Título"
              >
                <LuHeading2 />
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => applyFormat('formatBlock', 'h3')}
                title="Subtítulo"
              >
                <LuHeading3 />
              </button>
            </div>
          </>
        )}

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => applyFormat('insertUnorderedList')}
            title="Lista con viñetas"
          >
            <FiList />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => applyFormat('insertOrderedList')}
            title="Lista numerada"
          >
            <LuListOrdered />
          </button>
        </div>

        {!compact && (
          <>
            <div className="toolbar-divider" />
            <div className="toolbar-group">
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => applyFormat('insertHorizontalRule')}
                title="Línea horizontal"
              >
                <FiMinus />
              </button>
            </div>
          </>
        )}
      </div>

      <div
        ref={setEditorRef}
        className="wysiwyg-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{ minHeight }}
      />
    </div>
  );
};

export default WysiwygEditor;
